import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { customerService } from '../../services/customerService';
import type { CustomerTag } from '../../types/customer360';
import { Tag, Plus, Edit2, Trash2, X } from 'lucide-react';
import DeleteConfirmationModal from './DeleteConfirmationModal';

interface TagManagerProps {
  customerId?: number;
  selectedTags?: CustomerTag[];
  onTagsChange?: (tags: CustomerTag[]) => void;
  showAssignment?: boolean;
}

const defaultColors = [
  '#F59E0B', // Orange
  '#10B981', // Green
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#6366F1', // Indigo
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#F97316'  // Orange-red
];

export default function TagManager({
  customerId,
  selectedTags = [],
  onTagsChange,
  showAssignment = true
}: TagManagerProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { user } = useAuthContext();
  const { showToast } = useToast();
  const [allTags, setAllTags] = useState<CustomerTag[]>([]);
  const [customerTags, setCustomerTags] = useState<CustomerTag[]>(selectedTags);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState<CustomerTag | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tagPendingDelete, setTagPendingDelete] = useState<CustomerTag | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchTags();
    if (customerId) {
      fetchCustomerTags();
    }
  }, [customerId]);

  const fetchTags = async () => {
    try {
      const tags = await customerService.getAllTags();
      setAllTags(Array.isArray(tags) ? tags : []);
    } catch (error) {
      console.error('Error fetching tags:', error);
      showToast('Failed to load tags', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerTags = async () => {
    if (!customerId) return [] as CustomerTag[];

    try {
      const tags = await customerService.getCustomerTags(customerId);
      const tagArray = Array.isArray(tags) ? tags : [];
      setCustomerTags(tagArray);
      return tagArray;
    } catch (error) {
      console.error('Error fetching customer tags:', error);
      setCustomerTags([]);
      return [];
    }
  };

  const handleAssignTag = async (tagId: number) => {
    if (!customerId) return;

    try {
      await customerService.assignTag(customerId, {
        tag_id: tagId,
        assigned_by: user?.id
      });
      showToast('Tag assigned successfully', 'success');
      const updatedTags = await fetchCustomerTags();
      if (onTagsChange) {
        onTagsChange(updatedTags);
      }
    } catch (error) {
      console.error('Error assigning tag:', error);
      showToast('Failed to assign tag', 'error');
    }
  };

  const handleUnassignTag = async (tagId: number) => {
    if (!customerId) return;

    try {
      await customerService.unassignTag(customerId, tagId);
      showToast('Tag removed successfully', 'success');
      const updatedTags = await fetchCustomerTags();
      if (onTagsChange) {
        onTagsChange(updatedTags);
      }
    } catch (error) {
      console.error('Error removing tag:', error);
      showToast('Failed to remove tag', 'error');
    }
  };

  const handleCreateTag = () => {
    setEditingTag(null);
    setShowModal(true);
  };

  const handleEditTag = (tag: CustomerTag) => {
    setEditingTag(tag);
    setShowModal(true);
  };

  const requestDeleteTag = (tag: CustomerTag) => {
    setTagPendingDelete(tag);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setTagPendingDelete(null);
  };

  const handleDeleteTag = async () => {
    if (!tagPendingDelete) return;

    try {
      setIsDeleting(true);
      await customerService.deleteTag(tagPendingDelete.id);
      showToast('Tag deleted successfully', 'success');
      await fetchTags();
      if (customerId) {
        const updatedTags = await fetchCustomerTags();
        if (onTagsChange) {
          onTagsChange(updatedTags);
        }
      }
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting tag:', error);
      showToast('Failed to delete tag', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const isTagAssigned = (tagId: number) => {
    if (!Array.isArray(customerTags)) return false;
    return customerTags.some((tag) => tag.id === tagId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-semibold flex items-center gap-2 ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>
          <Tag className="h-5 w-5" />
          Tags
        </h3>
        {isAdmin && (
          <button
            onClick={handleCreateTag}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Tag
          </button>
        )}
      </div>

      {/* Customer Tags (if showing assignment) */}
      {showAssignment && customerId && (
        <div>
          <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Assigned Tags
          </h4>
          {!Array.isArray(customerTags) || customerTags.length === 0 ? (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              No tags assigned yet
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {customerTags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full text-white"
                  style={{ backgroundColor: tag.color }}
                >
                  <span className="text-sm font-medium">{tag.name}</span>
                  <button
                    onClick={() => handleUnassignTag(tag.id)}
                    className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                    title="Remove tag"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* All Available Tags */}
      <div>
        <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {showAssignment && customerId ? 'Available Tags' : 'All Tags'}
        </h4>
        {allTags.length === 0 ? (
          <div
            className={`text-center py-8 rounded-lg border-2 border-dashed ${
              isDark
                ? 'border-gray-700 text-gray-400'
                : 'border-gray-300 text-gray-500'
            }`}
          >
            <Tag className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>No tags available</p>
            {isAdmin && <p className="text-sm mt-1">Create your first tag to get started</p>}
          </div>
        ) : (
          <div className="space-y-2">
            {allTags.map((tag) => {
              const isAssigned = isTagAssigned(tag.id);
              return (
                <div
                  key={tag.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    <div className="flex-1">
                      <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {tag.name}
                      </div>
                      {tag.description && (
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {tag.description}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Assign/Unassign Button */}
                    {showAssignment && customerId && (
                      <button
                        onClick={() => isAssigned ? handleUnassignTag(tag.id) : handleAssignTag(tag.id)}
                        className={`px-3 py-1 text-sm rounded transition-colors ${
                          isAssigned
                            ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}
                      >
                        {isAssigned ? 'Remove' : 'Assign'}
                      </button>
                    )}

                    {/* Edit Button (Admin Only) */}
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => handleEditTag(tag)}
                          className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                          title="Edit tag"
                        >
                          <Edit2 className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                        </button>
                        <button
                          onClick={() => requestDeleteTag(tag)}
                          className="p-2 rounded hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
                          title="Delete tag"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tag Modal */}
      {showModal && (
        <TagModal
          tag={editingTag}
          onClose={() => {
            setShowModal(false);
            setEditingTag(null);
          }}
          onSave={() => {
            fetchTags();
            setShowModal(false);
            setEditingTag(null);
          }}
        />
      )}

      {deleteModalOpen && tagPendingDelete && (
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          title="Delete Tag"
          description={
            <>
              Are you sure you want to delete the tag <strong>{tagPendingDelete.name}</strong>? This will
              remove it from the available tag list and unassign it from all customers.
            </>
          }
          confirmLabel="Delete Tag"
          isProcessing={isDeleting}
          onCancel={closeDeleteModal}
          onConfirm={handleDeleteTag}
          previewBadge={{ label: tagPendingDelete.name, color: tagPendingDelete.color }}
        />
      )}
    </div>
  );
}

// Tag Modal Component
function TagModal({
  tag,
  onClose,
  onSave
}: {
  tag: CustomerTag | null;
  onClose: () => void;
  onSave: () => void;
}) {
  const { theme } = useTheme();
  const { user } = useAuthContext();
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);

  const isDark = theme === 'dark';

  const [formData, setFormData] = useState({
    name: tag?.name || '',
    color: tag?.color || defaultColors[0],
    description: tag?.description || ''
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (tag) {
        await customerService.updateTag(tag.id, formData);
        showToast('Tag updated successfully', 'success');
      } else {
        await customerService.createTag({
          ...formData,
          created_by: user?.id
        });
        showToast('Tag created successfully', 'success');
      }
      onSave();
    } catch (error) {
      console.error('Error saving tag:', error);
      showToast(`Failed to ${tag ? 'update' : 'create'} tag`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className={`relative rounded-lg shadow-xl max-w-md w-full ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {tag ? 'Edit Tag' : 'Create New Tag'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <X className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Tag Name */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Tag Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              placeholder="e.g., Premium Customer"
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          {/* Color Picker */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Color
            </label>
            <div className="flex gap-2 mb-2">
              {defaultColors.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleChange('color', color)}
                  className={`w-8 h-8 rounded-full transition-transform ${
                    formData.color === color ? 'scale-110 ring-2 ring-offset-2 ring-blue-500' : ''
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
            <input
              type="color"
              value={formData.color}
              onChange={(e) => handleChange('color', e.target.value)}
              className="w-full h-10 rounded cursor-pointer"
            />
          </div>

          {/* Description */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief description of this tag"
              rows={3}
              className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          {/* Preview */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Preview
            </label>
            <div
              className="inline-flex items-center px-3 py-1.5 rounded-full text-white text-sm font-medium"
              style={{ backgroundColor: formData.color }}
            >
              {formData.name || 'Tag Name'}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className={`px-6 py-2 rounded-lg border transition-colors ${
                isDark
                  ? 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.name}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : tag ? 'Update Tag' : 'Create Tag'}
            </button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
